import React, { useEffect, useState } from 'react';
import { Image, Skeleton, Pressable, Box } from 'native-base';
import { Keyboard, Mousewheel, Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import {
    MenuSectionFilterType,
    ProductListPageProps,
    Special,
    useDutchieStore,
    useSpecialsByRetailerIdQuery,
    useTheme,
} from '@springbig/shared';
import { isBefore, isAfter, format, parse } from 'date-fns';
import { useAppNavigate } from '~/pages';
import { GenericSpecial } from '~/pages/shop/components/GenericSpecial';

const swiperStyles = {
    width: '85%',
    borderRadius: '8px',
    '--swiper-pagination-color': 'white',
    '--swiper-pagination-bullet-inactive-color': 'white',
};

export const PromotionCarousel = () => {

    const {
        radius_md,
    } = useTheme();

    const { mutate: getSpecials, isLoading } = useSpecialsByRetailerIdQuery();
    const {
        retailer,
        menuType,
        setProductListProps,
        isBusy,
    } = useDutchieStore();

    const { navigate, params } = useAppNavigate();

    const [curatedSpecials, setCuratedSpecials] = useState<Array<Special>>();

    useEffect(() => {

        if (retailer && !curatedSpecials && !isLoading) {
            const variables = {
                'retailerId': retailer.id,
            };
            getSpecials(variables, {
                onSuccess: (response) => {

                    curateSpecials(response.specials);
                },
            });
        }
    }, [retailer, getSpecials]);

    const curateSpecials = (specials: Array<Special>): void => {

        // display specials based on date, hour, and menuType
        const _curatedSpecials = specials?.reduce((accumulator: Array<Special>, special) => {

            const isActiveMenuType = (
                (special.menuType as 'BOTH' | 'RECREATIONAL' | 'MEDICAL') === menuType ||
                special.menuType === 'BOTH'
            );

            const isActiveDate = (
                isAfter(new Date(), new Date(special.scheduleConfiguration.startStamp)) &&
                (
                    isBefore(new Date(), new Date(special.scheduleConfiguration.endStamp)) ||
                    !special.scheduleConfiguration.endStamp
                )
            );

            const todayIndex = parseInt(format(new Date(), 'c'));
            const isActiveDay = (
                !special.scheduleConfiguration.days ||
                special.scheduleConfiguration.days.includes(todayIndex)
            );

            const isRecurring = (
                special.scheduleConfiguration.recurringStartTime &&
                special.scheduleConfiguration.recurringEndTime
            );

            const parsedRecurringStartTime = parse(
                special.scheduleConfiguration.recurringStartTime, 'h:mm a', new Date()
            );
            const parsedRecurringEndTime = parse(
                special.scheduleConfiguration.recurringEndTime, 'h:mm a', new Date()
            );

            const isActiveHours = (
                (
                    isAfter(new Date(), parsedRecurringStartTime) &&
                    isBefore(new Date(), parsedRecurringEndTime)
                ) || !isRecurring
            );

            if (isActiveMenuType && isActiveDate && isActiveHours && isActiveDay) {
                accumulator.push(special);
            }

            return accumulator;
        }, []);

        setCuratedSpecials(_curatedSpecials);
    };

    const parseUrl = (url: string): string | boolean => {

        // some images are supposed to be locally sourced from
        // '/images/default-special-card.jpg'
        // sending false until we get a generic image from PO
        return url?.includes('https://') ? url + '?w=480' : false;
    };

    const onSelectSpecial = (special: Special): void => {

        const productListProps: ProductListPageProps = {
            title: special.name,
            menuSection: { type: 'SPECIALS' as MenuSectionFilterType, specialId: [special.id] },
        };
        setProductListProps(productListProps);
        navigate(`w/${params.wallet_id}/shop/product_list`);
    };

    if (isLoading) {
        return (
            <Skeleton
                my={2}
                h={'120px'}
                w={'90%'}
                alignSelf={'center'}
                rounded={radius_md}
            />
        );
    }

    return (
        <>
            { curatedSpecials?.length > 0 ?
                <Box py={2}>
                    <Swiper
                        spaceBetween={0}
                        navigation={false}
                        pagination={{
                            clickable: true,
                            type: 'bullets',
                        }}
                        keyboard={true}
                        modules={[Pagination, Mousewheel, Keyboard]}
                        className="mySwiper"
                        style={swiperStyles}
                    >
                        { curatedSpecials?.length && curatedSpecials.map((el, index) =>

                            <SwiperSlide
                                style={{
                                    width: '100%',
                                    height: '100%',
                                }}
                                key={index}
                            >
                                <Pressable
                                    opacity={isBusy ? .7 : 1}
                                    isDisabled={isBusy}
                                    onPress={() => onSelectSpecial(el)}
                                >
                                    { parseUrl(el.menuDisplayConfiguration.image) ?
                                        <Image
                                            width={'100%'}
                                            height={'120px'}
                                            src={parseUrl(el.menuDisplayConfiguration.image) as string}
                                        />
                                        :
                                        <GenericSpecial name={el.menuDisplayConfiguration.name} />
                                    }
                                </Pressable>
                            </SwiperSlide>
                        )}
                    </Swiper>
                </Box>
                :
                <></>
            }
        </>
    );
};
